/* eslint-disable camelcase */
import { useUserId } from '@nhost/react';
import React, { ElementType, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetRestaurantByUserIdQuery, useUpdateRestaurantMutation } from 'src/utils/__generated__/graphql';
import { Field, labelMap, ModernCard, PageHeader } from './shared';
import { useGetImageUrl } from 'src/hooks';
import { nhost } from 'src/lib/nhost';
import {
  Flex,
  Heading,
  Button,
  Box,
  FormControl,
  FormLabel,
  SimpleGrid,
  Input,
  NumberInput,
  NumberInputField,
  Switch,
  Textarea,
  Image,
  useToast,
  Text,
  Badge,
  VStack,
  HStack,
  Icon,
  Card,
  CardBody,
} from '@chakra-ui/react';
import { compressImage, convertSnakeCaseToWords } from 'src/utils';
import { FiUpload, FiArrowLeft, FiSave } from 'react-icons/fi';

const fieldNames = ['description', 'short_description', 'threshold_amount', 'min_order_amount', 'isActive'];

export const EditSellerRestaurant = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const userId = useUserId();

  const {
    data,
    loading,
    error,
    refetch: refetchRestaurant,
  } = useGetRestaurantByUserIdQuery({
    variables: { userId },
  });

  const [updateRestaurant, { loading: updateLoading, error: updateError }] = useUpdateRestaurantMutation({
    onCompleted: () => refetchRestaurant(),
  });

  const [formValues, setFormValues] = useState<Field[]>(
    fieldNames.map((fieldName) => ({
      label: fieldName,
      value: fieldName === 'isActive' ? false : '',
    }))
  );

  const restaurantData = data?.restaurant?.[0];
  const { imageUrl: logoUrl } = useGetImageUrl(restaurantData?.image);
  const { imageUrl: coverUrl } = useGetImageUrl(restaurantData?.cover);

  useEffect(() => {
    if (restaurantData) {
      setFormValues((prevValues) =>
        prevValues.map((field) => ({
          ...field,
          value:
            field.label === 'isActive'
              ? Boolean(restaurantData[field.label])
              : (restaurantData as any)[field.label] || '',
        }))
      );
    }
  }, [restaurantData]);

  const handleInputChange = (label: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = e.target;
    setFormValues((prevValues) => prevValues.map((field) => (field.label === label ? { ...field, value } : field)));
  };

  const hasFormChanged = () => {
    return formValues.some((field) => field.value !== ((restaurantData as any)[field.label] || ''));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!hasFormChanged()) {
      toast({
        title: 'No changes detected',
        status: 'info',
        duration: 2000,
      });
      return;
    }

    const updatedValues = formValues.reduce(
      (acc, field) => {
        acc[field.label] = field.value;
        return acc;
      },
      {} as Record<string, string | number>
    );

    try {
      await updateRestaurant({
        variables: {
          pk_columns: { id: restaurantData?.id },
          _set: updatedValues,
        },
      });
      toast({
        title: 'Restaurant Updated',
        description: 'Changes saved successfully',
        status: 'success',
        duration: 3000,
      });
      refetchRestaurant();
    } catch (error: any) {
      toast({
        title: 'Update Failed',
        description: error?.message || 'An error occurred',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const imageType = type === 'logo' ? 'image' : 'cover';
    const previousFileId = type === 'logo' ? restaurantData?.image : restaurantData?.cover;

    try {
      if (previousFileId) {
        await nhost.storage.delete({ fileId: previousFileId });
      }

      const compressedFile = await compressImage(file);
      const result = await nhost.storage.upload({
        file: compressedFile,
        bucketId: 'default',
      });

      if (result) {
        await updateRestaurant({
          variables: {
            pk_columns: { id: restaurantData?.id },
            _set: {
              [imageType]: result?.fileMetadata?.id ?? null,
            },
          },
        });
        toast({
          title: 'Image Updated',
          status: 'success',
          duration: 2000,
        });
      }
    } catch (error: any) {
      toast({
        title: `Failed to upload ${type}`,
        description: error?.message,
        status: 'error',
        duration: 3000,
      });
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error.message} />;
  if (updateLoading) return <LoadingState message="Saving changes..." />;
  if (updateError) return <ErrorState message={updateError.message} />;

  return (
    <>
      <PageHeader
        title="Edit Restaurant"
        subtitle="Update your restaurant information"
        actions={
          <Button
            leftIcon={<Icon as={FiArrowLeft as ElementType} />}
            colorScheme="gray"
            variant="outline"
            onClick={() => navigate(-1)}
            size={{ base: 'md', md: 'lg' }}
          >
            <Text display={{ base: 'none', sm: 'block' }}>Back to Restaurant</Text>
          </Button>
        }
      />
      <ModernCard p={{ base: 6, md: 8 }}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          {/* Images Section */}
          <VStack spacing={6}>
            <ImageUploadCard
              title="Restaurant Logo"
              imageUrl={logoUrl}
              onChange={(e) => handleFileChange(e, 'logo')}
              placeholderSrc="/assets/img/logo-placeholder.jpg"
            />
            <ImageUploadCard
              title="Cover Image"
              imageUrl={coverUrl}
              onChange={(e) => handleFileChange(e, 'cover')}
              placeholderSrc="/assets/img/cover-placeholder.jpg"
            />
          </VStack>

          {/* Form Fields */}
          <VStack spacing={4} align="stretch">
            {formValues.map((field) => (
              <FormControl key={field.label}>
                <FormLabel>{labelMap[field.label] || convertSnakeCaseToWords(field.label)}</FormLabel>
                {field.label === 'description' || field.label === 'short_description' ? (
                  <Textarea
                    value={field.value as string}
                    onChange={handleInputChange(field.label)}
                    resize="vertical"
                    minH="100px"
                  />
                ) : field.label === 'isActive' ? (
                  <HStack>
                    <Switch
                      isChecked={field.value as boolean}
                      size="lg"
                      colorScheme="teal"
                      onChange={(e) => {
                        handleInputChange(field.label)({
                          target: { value: e.target.checked },
                        } as any);
                      }}
                    />
                    <Badge colorScheme={field.value ? 'green' : 'red'}>{field.value ? 'Active' : 'Inactive'}</Badge>
                  </HStack>
                ) : ['threshold_amount', 'min_order_amount'].includes(field.label) ? (
                  <NumberInput value={field.value as number}>
                    <NumberInputField onChange={handleInputChange(field.label)} />
                  </NumberInput>
                ) : (
                  <Input value={field.value as string} onChange={handleInputChange(field.label)} />
                )}
              </FormControl>
            ))}
          </VStack>
        </SimpleGrid>

        <Flex justify="flex-end" mt={8}>
          <Button
            leftIcon={<Icon as={FiSave as any} />}
            colorScheme="teal"
            size="lg"
            onClick={handleSubmit}
            isDisabled={!hasFormChanged()}
            loadingText="Saving..."
            isLoading={updateLoading}
          >
            Save Changes
          </Button>
        </Flex>
      </ModernCard>
    </>
  );
};

// Helper Components
const ImageUploadCard = ({
  title,
  imageUrl,
  onChange,
  placeholderSrc,
}: {
  title: string;
  imageUrl?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholderSrc: string;
}) => (
  <Card width="100%">
    <CardBody>
      <VStack spacing={4}>
        <Heading size="sm">{title}</Heading>
        <Box position="relative" width="100%" height="200px" borderRadius="md" overflow="hidden">
          <Image src={imageUrl || placeholderSrc} alt={title} objectFit="cover" width="100%" height="100%" />
          <Box position="absolute" bottom={0} left={0} right={0} p={2} bg="blackAlpha.600" backdropFilter="blur(8px)">
            <Button
              as="label"
              width="100%"
              cursor="pointer"
              leftIcon={<Icon as={FiUpload as any} />}
              colorScheme="teal"
              variant="ghost"
              _hover={{ bg: 'blackAlpha.400' }}
            >
              Upload New Image
              <input type="file" onChange={onChange} style={{ display: 'none' }} accept="image/*" />
            </Button>
          </Box>
        </Box>
      </VStack>
    </CardBody>
  </Card>
);

const LoadingState = ({ message = 'Loading...' }) => (
  <Flex height="100vh" align="center" justify="center">
    <Text fontSize="xl" color="gray.500">
      {message}
    </Text>
  </Flex>
);

const ErrorState = ({ message }: { message: string }) => (
  <Flex height="100vh" align="center" justify="center">
    <Text color="red.500">{message}</Text>
  </Flex>
);

// // Image compression utility
// const compressImage = (file: File): Promise<File> => {
//   return new Promise((resolve) => {
//     const reader = new FileReader();
//     reader.onload = (e) => {
//       const img = document.createElement('img');
//       img.onload = () => {
//         const canvas = document.createElement('canvas');
//         const MAX_WIDTH = 800;
//         const MAX_HEIGHT = 800;
//         let width = img.width;
//         let height = img.height;

//         if (width > height) {
//           if (width > MAX_WIDTH) {
//             height *= MAX_WIDTH / width;
//             width = MAX_WIDTH;
//           }
//         } else {
//           if (height > MAX_HEIGHT) {
//             width *= MAX_HEIGHT / height;
//             height = MAX_HEIGHT;
//           }
//         }

//         canvas.width = width;
//         canvas.height = height;
//         const ctx = canvas.getContext('2d');
//         ctx?.drawImage(img, 0, 0, width, height);

//         canvas.toBlob(
//           (blob) => {
//             if (blob) {
//               resolve(new File([blob], file.name, { type: 'image/jpeg' }));
//             }
//           },
//           'image/jpeg',
//           0.7
//         );
//       };
//       img.src = e.target?.result as string;
//     };
//     reader.readAsDataURL(file);
//   });
// };
